import { db } from '@/firebaseConfig';
import {
  collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, query, where, arrayUnion, arrayRemove
} from 'firebase/firestore';
import { LEAGUES_DB, SAVED_TOURNAMENT_RESULTS_DB } from '@/constants/constants.js';

/**
 * Create a new league.
 * @param {{ name: string, ownerId: string, ownerAlias: string, settings?: { rebuyPenalty: number } }} leagueData
 * @returns {Promise<string>} - The new league's Firestore document ID
 */
export async function createLeague({ name, ownerId, ownerAlias, settings = {} }) {
  const payload = {
    name: (name || '').trim(),
    ownerId,
    ownerAlias,
    createdAt: Date.now(),
    settings: {
      rebuyPenalty: typeof settings.rebuyPenalty === 'number' ? settings.rebuyPenalty : 1,
    },
    allowedRunners: [],
  };
  const ref = await addDoc(collection(db, LEAGUES_DB), payload);
  return ref.id;
}

/**
 * Fetch a single league by its Firestore document ID.
 * @param {string} leagueId
 * @returns {Promise<object|null>} - League object with `id` field, or null if not found
 */
export async function getLeague(leagueId) {
  if (!leagueId) return null;
  try {
    const snap = await getDoc(doc(db, LEAGUES_DB, leagueId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (e) {
    console.error('getLeague error:', e);
    return null;
  }
}

/**
 * Get all leagues the given runner owns OR has been invited to.
 * Runs two Firestore queries and merges results, deduplicating by doc ID.
 * @param {string} userId - Firestore user_id of the runner
 * @returns {Promise<object[]>}
 */
export async function getLeaguesForRunner(userId) {
  if (!userId) return [];
  try {
    const col = collection(db, LEAGUES_DB);
    const [ownedSnap, invitedSnap] = await Promise.all([
      getDocs(query(col, where('ownerId', '==', userId))),
      getDocs(query(col, where('allowedRunners', 'array-contains', userId))),
    ]);
    const map = new Map();
    for (const snap of [...ownedSnap.docs, ...invitedSnap.docs]) {
      if (!map.has(snap.id)) {
        map.set(snap.id, { id: snap.id, ...snap.data() });
      }
    }
    return Array.from(map.values());
  } catch (e) {
    console.error('getLeaguesForRunner error:', e);
    return [];
  }
}

/**
 * Update a league's settings (rebuyPenalty, name, etc.).
 * @param {string} leagueId
 * @param {{ name?: string, settings?: object }} updates
 */
export async function updateLeague(leagueId, updates) {
  if (!leagueId) return;
  await updateDoc(doc(db, LEAGUES_DB, leagueId), updates);
}

/**
 * Add a runner UID to the league's allowedRunners list.
 * @param {string} leagueId
 * @param {string} runnerUid - user_id (Firebase Auth UID) of the runner to add
 */
export async function addRunnerToLeague(leagueId, runnerUid) {
  if (!leagueId || !runnerUid) return;
  await updateDoc(doc(db, LEAGUES_DB, leagueId), {
    allowedRunners: arrayUnion((runnerUid || '').trim()),
  });
}

/**
 * Remove a runner UID from the league's allowedRunners list.
 * @param {string} leagueId
 * @param {string} runnerUid - user_id (Firebase Auth UID) of the runner to remove
 */
export async function removeRunnerFromLeague(leagueId, runnerUid) {
  if (!leagueId || !runnerUid) return;
  await updateDoc(doc(db, LEAGUES_DB, leagueId), {
    allowedRunners: arrayRemove((runnerUid || '').trim()),
  });
}

/**
 * Get all saved tournament results belonging to a league.
 * @param {string} leagueId
 * @returns {Promise<object[]>} - Array of result docs with `id` field
 */
export async function getLeagueTournaments(leagueId) {
  if (!leagueId) return [];
  try {
    const col = collection(db, SAVED_TOURNAMENT_RESULTS_DB);
    const snap = await getDocs(query(col, where('leagueId', '==', leagueId)));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('getLeagueTournaments error:', e);
    return [];
  }
}

/**
 * Delete a league by its Firestore document ID.
 * @param {string} leagueId
 */
export async function deleteLeague(leagueId) {
  if (!leagueId) return;
  await deleteDoc(doc(db, LEAGUES_DB, leagueId));
}
